/**
 * Google Analytics 4 Custom Event Tracking Utility
 * Provides easy-to-use functions for tracking user interactions
 */

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a custom event in GA4
 * @param {string} eventName - The name of the event
 * @param {object} params - Additional parameters for the event
 */
export const trackEvent = (eventName, params = {}) => {
  if (isGtagAvailable()) {
    window.gtag('event', eventName, params);
    console.log(`[GA4] Event tracked: ${eventName}`, params);
  }
};

// ============================================
// NAVIGATION TRACKING
// ============================================

/**
 * Track navigation menu clicks
 * @param {string} menuItem - The menu item clicked
 * @param {string} location - Where the click happened (header, footer, mobile)
 */
export const trackNavigation = (menuItem, location = 'header') => {
  trackEvent('navigation_click', {
    menu_item: menuItem,
    click_location: location,
  });
};

/**
 * Track page views (for SPA navigation)
 * @param {string} pagePath - The page path
 * @param {string} pageTitle - The page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (isGtagAvailable()) {
    window.gtag('config', 'G-BBGY590NGH', {
      page_path: pagePath,
      page_title: pageTitle,
    });
    console.log(`[GA4] Page view tracked: ${pagePath}`);
  }
};

// ============================================
// BUTTON & CTA TRACKING
// ============================================

/**
 * Track CTA button clicks
 * @param {string} buttonName - Name of the button
 * @param {string} buttonLocation - Where the button is located
 */
export const trackButtonClick = (buttonName, buttonLocation = 'unknown') => {
  trackEvent('cta_click', {
    button_name: buttonName,
    button_location: buttonLocation,
  });
};

/**
 * Track "Contact Now" button clicks
 * @param {string} location - Where the button was clicked from
 */
export const trackContactClick = (location = 'header') => {
  trackEvent('contact_initiated', {
    click_location: location,
    event_category: 'engagement',
  });
};

/**
 * Track external link clicks
 * @param {string} linkUrl - The URL being clicked
 * @param {string} linkText - The text of the link
 */
export const trackExternalLink = (linkUrl, linkText) => {
  trackEvent('external_link_click', {
    link_url: linkUrl,
    link_text: linkText,
  });
};

// ============================================
// CONTACT FORM TRACKING
// ============================================

/**
 * Track contact modal open
 * @param {string} trigger - What triggered the modal opening
 */
export const trackModalOpen = (trigger = 'button') => {
  trackEvent('modal_open', {
    modal_type: 'contact_form',
    trigger_source: trigger,
  });
};

/**
 * Track contact modal close
 * @param {number} step - The step at which the modal was closed
 */
export const trackModalClose = (step = 1) => {
  trackEvent('modal_close', {
    modal_type: 'contact_form',
    closed_at_step: step,
  });
};

/**
 * Track contact form step progress
 * @param {number} stepNumber - The step number (1-5)
 * @param {string} stepName - The name of the step
 */
export const trackFormStep = (stepNumber, stepName) => {
  trackEvent('form_step_progress', {
    step_number: stepNumber,
    step_name: stepName,
    form_name: 'contact_form',
  });
};

/**
 * Track contact form submission
 * @param {object} formData - Relevant form data (no PII)
 */
export const trackFormSubmission = (formData = {}) => {
  trackEvent('form_submission', {
    form_name: 'contact_form',
    preferred_contact: formData.preferredContact || 'not_specified',
    urgency: formData.urgency || 'not_specified',
    referral_source: formData.referralSource || 'not_specified',
    possession_timeline: formData.possessionTimeline || 'not_specified',
    has_brochure_request: formData.sendBrochure || false,
    event_category: 'conversion',
  });
};

/**
 * Track callback form submission
 * @param {object} formData - Relevant form data (no PII)
 */
export const trackCallbackSubmission = (formData = {}) => {
  trackEvent('callback_request', {
    form_name: 'callback_form',
    industry: formData.industry || 'not_specified',
    urgency: formData.urgency || 'not_specified',
    event_category: 'conversion',
  });
};

// ============================================
// USER PREFERENCE TRACKING
// ============================================

/**
 * Track profile tag selection
 * @param {string} tag - The tag selected
 * @param {boolean} selected - Whether it was selected or deselected
 */
export const trackProfileTagSelection = (tag, selected) => {
  trackEvent('profile_tag_selection', {
    tag_name: tag,
    action: selected ? 'selected' : 'deselected',
  });
};

/**
 * Track contact preference selection
 * @param {string} preference - The preference selected (Call, WhatsApp, Email)
 */
export const trackContactPreference = (preference) => {
  trackEvent('contact_preference_selected', {
    preference_type: preference,
  });
};

/**
 * Track time slot selection
 * @param {string} timeSlot - The selected time slot
 */
export const trackTimeSlotSelection = (timeSlot) => {
  trackEvent('time_slot_selected', {
    time_slot: timeSlot,
  });
};

// ============================================
// ENGAGEMENT TRACKING
// ============================================

/**
 * Track scroll depth
 * @param {number} percentage - The scroll depth percentage
 */
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll_depth', {
    depth_percentage: percentage,
    event_category: 'engagement',
  });
};

/**
 * Track section view
 * @param {string} sectionName - The section being viewed
 */
export const trackSectionView = (sectionName) => {
  trackEvent('section_view', {
    section_name: sectionName,
    event_category: 'engagement',
  });
};

/**
 * Track gallery interaction
 * @param {string} action - The action taken (view, next, prev, zoom)
 * @param {number} imageIndex - The index of the image
 */
export const trackGalleryInteraction = (action, imageIndex = 0) => {
  trackEvent('gallery_interaction', {
    action_type: action,
    image_index: imageIndex,
  });
};

// ============================================
// SOCIAL TRACKING
// ============================================

/**
 * Track social link clicks
 * @param {string} platform - The social platform (facebook, instagram, linkedin)
 */
export const trackSocialClick = (platform) => {
  trackEvent('social_click', {
    platform: platform,
    event_category: 'engagement',
  });
};

// ============================================
// MAP & LOCATION TRACKING
// ============================================

/**
 * Track map interaction
 * @param {string} action - The action (view, zoom, directions)
 */
export const trackMapInteraction = (action) => {
  trackEvent('map_interaction', {
    action_type: action,
  });
};

// ============================================
// SCROLL TO TOP TRACKING
// ============================================

/**
 * Track scroll to top button click
 */
export const trackScrollToTop = () => {
  trackEvent('scroll_to_top', {
    event_category: 'engagement',
  });
};

// ============================================
// SCROLL DEPTH TRACKING (Enhanced)
// ============================================

// Track which scroll milestones have been reached
const scrollMilestones = { 25: false, 50: false, 75: false, 100: false };

/**
 * Initialize scroll depth tracking
 * Call this once when the app loads
 */
export const initScrollDepthTracking = () => {
  if (typeof window === 'undefined') return;

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);

    // Track milestones: 25%, 50%, 75%, 100%
    [25, 50, 75, 100].forEach((milestone) => {
      if (scrollPercent >= milestone && !scrollMilestones[milestone]) {
        scrollMilestones[milestone] = true;
        trackEvent('scroll_depth_milestone', {
          depth_percentage: milestone,
          event_category: 'engagement',
        });
        console.log(`[GA4] Scroll depth milestone: ${milestone}%`);
      }
    });
  };

  // Debounce scroll handler for performance
  let scrollTimeout;
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(handleScroll, 100);
  });
};

/**
 * Reset scroll milestones (call on page navigation)
 */
export const resetScrollMilestones = () => {
  Object.keys(scrollMilestones).forEach((key) => {
    scrollMilestones[key] = false;
  });
};

// ============================================
// SECTION VISIBILITY TRACKING
// ============================================

/**
 * Initialize section visibility tracking using Intersection Observer
 * Tracks when users view specific sections of the page
 */
export const initSectionVisibilityTracking = () => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

  const trackedSections = new Set();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id || entry.target.dataset.section;
          if (sectionId && !trackedSections.has(sectionId)) {
            trackedSections.add(sectionId);
            trackEvent('section_visible', {
              section_name: sectionId,
              event_category: 'engagement',
            });
            console.log(`[GA4] Section viewed: ${sectionId}`);
          }
        }
      });
    },
    {
      threshold: 0.5, // Trigger when 50% of section is visible
    }
  );

  // Observe all sections with id or data-section attribute
  setTimeout(() => {
    const sections = document.querySelectorAll('section[id], [data-section]');
    sections.forEach((section) => observer.observe(section));
  }, 1000); // Delay to ensure DOM is ready

  return observer;
};

// ============================================
// TIME ON PAGE TRACKING
// ============================================

let pageLoadTime = null;
let timeTrackingIntervals = [30, 60, 120, 300]; // seconds
let trackedIntervals = new Set();

/**
 * Initialize time on page tracking
 */
export const initTimeOnPageTracking = () => {
  if (typeof window === 'undefined') return;

  pageLoadTime = Date.now();
  trackedIntervals.clear();

  const checkTimeOnPage = () => {
    if (!pageLoadTime) return;
    
    const timeSpent = Math.floor((Date.now() - pageLoadTime) / 1000);
    
    timeTrackingIntervals.forEach((interval) => {
      if (timeSpent >= interval && !trackedIntervals.has(interval)) {
        trackedIntervals.add(interval);
        trackEvent('time_on_page', {
          seconds: interval,
          event_category: 'engagement',
        });
        console.log(`[GA4] Time on page: ${interval}s`);
      }
    });
  };

  // Check every 10 seconds
  setInterval(checkTimeOnPage, 10000);
};

/**
 * Reset time tracking (call on page navigation)
 */
export const resetTimeTracking = () => {
  pageLoadTime = Date.now();
  trackedIntervals.clear();
};

export default {
  trackEvent,
  trackNavigation,
  trackPageView,
  trackButtonClick,
  trackContactClick,
  trackExternalLink,
  trackModalOpen,
  trackModalClose,
  trackFormStep,
  trackFormSubmission,
  trackCallbackSubmission,
  trackProfileTagSelection,
  trackContactPreference,
  trackTimeSlotSelection,
  trackScrollDepth,
  trackSectionView,
  trackGalleryInteraction,
  trackSocialClick,
  trackMapInteraction,
  trackScrollToTop,
};
